/**
 * Local notifications — the rest timer's alarm.
 *
 * ── EXACT OR INEXACT IS DECIDED HERE, NOT BY THE PLUGIN. ──────────────────
 * Since Android 12 an exact alarm needs `SCHEDULE_EXACT_ALARM`, which the user
 * (or an OEM) can switch off. The plugin's default when it is off is to OPEN
 * the system "Alarms & reminders" screen in the middle of `schedule()` — a
 * settings page thrown up mid-set. So `scheduleNotification` checks first, and
 * without the grant schedules inexact outright and SAYS so in its result. The
 * caller decides whether to ask (`requestExactAlarms`), typically once at
 * launch, never silently. An inexact alarm can land minutes late under Doze.
 *
 * Revoking the grant restarts the app and deletes every exact alarm already
 * scheduled, so `exactAlarmState()` belongs in the launch path.
 *
 * Push (FCM) is not here; that is T21's.
 */
import { NOTIFICATION_CHANNEL_REST } from "@repo/contract/notifications";
import { isNative } from "./platform";

/** Android's IMPORTANCE_HIGH — the one that peeks. Frozen at creation on every
 *  device that already has the channel, so it can never be raised later.
 *  `as const` because the plugin's `Importance` is a literal union. */
const IMPORTANCE_HIGH = 4 as const;

/** The channel's name in the system settings list, when the caller gives none.
 *  English on purpose: it is the same fallback `push.ts` uses. */
const REST_CHANNEL_FALLBACK_NAME = "Rest timer";

/** Created once per process, not once per rest. `createChannel` is idempotent
 *  — re-creating an existing id updates its name and nothing else — but it is
 *  a bridge call, and a rest is armed between every set. */
let restChannelReady: Promise<void> | undefined;

type LocalNotificationsPlugin =
  typeof import("@capacitor/local-notifications")["LocalNotifications"];

/**
 * Exported for `__tests__/notifications.test.ts`, which is the only thing that
 * can see the three properties below — `scheduleNotification` around it is
 * unreachable off a device, because it returns early on `isNative()` and then
 * needs a real bridge. It takes its plugin as a PARAMETER for the same reason
 * (`D05`: injection, not a mocking library). It has a real caller one screen
 * down; an export with only a test behind it would prove nothing (`T-13`'s
 * neighbouring lesson).
 */
export function ensureRestChannel(
  plugin: Pick<LocalNotificationsPlugin, "createChannel">,
  name: string | undefined
): Promise<void> {
  restChannelReady ??= plugin
    .createChannel({
      id: NOTIFICATION_CHANNEL_REST,
      importance: IMPORTANCE_HIGH,
      name: name ?? REST_CHANNEL_FALLBACK_NAME,
    })
    // A failed creation must not stop the alarm: Android then delivers it on
    // the fallback channel, which is the old behaviour rather than silence.
    .catch(() => undefined);
  return restChannelReady;
}

/**
 * The rest notification's two buttons (`D209`, B9): "Concluir série" logs
 * the set the person just did without opening the app, and "Pular descanso"
 * ends the rest. Registered with `registerActionTypes` — a JS call, no native
 * change — under one type id the scheduled notification names.
 */
export const REST_ACTION_TYPE_ID = "mypocketgym.rest";
export const REST_ACTION_COMPLETE = "complete";
export const REST_ACTION_SKIP = "skip";

export interface RestActionTitles {
  readonly complete: string;
  readonly skip: string;
}

/** The titles last registered, so a rest per set is not a bridge call per set. */
let restActionsFor: string | undefined;

/**
 * Registers the two buttons under {@link REST_ACTION_TYPE_ID}, again only
 * when their words change (a language switch). Exported, with its plugin a
 * PARAMETER, for the same reason as {@link ensureRestChannel}. A failure
 * leaves a notification with no buttons, which is the old behaviour.
 */
export async function ensureRestActions(
  plugin: Pick<LocalNotificationsPlugin, "registerActionTypes">,
  titles: RestActionTitles
): Promise<void> {
  const key = `${titles.complete}\u0000${titles.skip}`;
  if (restActionsFor === key) {
    return;
  }
  try {
    await plugin.registerActionTypes({
      types: [
        {
          actions: [
            { id: REST_ACTION_COMPLETE, title: titles.complete },
            { id: REST_ACTION_SKIP, title: titles.skip },
          ],
          id: REST_ACTION_TYPE_ID,
        },
      ],
    });
    restActionsFor = key;
  } catch (error) {
    console.warn("[native] rest notification actions not registered", error);
  }
}

/**
 * Calls `listener` with the action id ("complete", "skip", or "tap" for the
 * notification's body) whenever a LOCAL notification of `notificationId` is
 * acted on. Returns the unsubscribe. Web: nothing to listen to.
 *
 * Capacitor delivers an action that opened the app as soon as this listener
 * is added (`retainUntilConsumed`), so a cold start from the button still
 * reaches it.
 */
export function onLocalNotificationAction(
  notificationId: number,
  listener: (actionId: string) => void
): () => void {
  if (!isNative()) {
    return () => undefined;
  }
  let removed = false;
  let remove: (() => Promise<void>) | undefined;
  const attach = async () => {
    const { LocalNotifications } = await import(
      "@capacitor/local-notifications"
    );
    const handle = await LocalNotifications.addListener(
      "localNotificationActionPerformed",
      (event) => {
        if (event.notification.id === notificationId) {
          listener(event.actionId);
        }
      }
    );
    remove = () => handle.remove();
    if (removed) {
      await remove();
    }
  };
  attach().catch((error: unknown) => {
    console.warn("[native] notification action listener failed", error);
  });
  return () => {
    removed = true;
    remove?.().catch(() => undefined);
  };
}

export type PermissionState = "denied" | "granted" | "prompt" | "unsupported";

export interface NotificationRequest {
  /** The buttons it carries (`D209`, B9); absent is none. */
  readonly actions?: RestActionTitles;
  readonly at: Date;
  readonly body: string;
  /** The channel's name as the person sees it in Android's settings, in the
   *  current locale. Omitted falls back to English, as push does. */
  readonly channelName?: string;
  /** A 32-bit integer on Android. Re-using an id replaces that notification. */
  readonly id: number;
  readonly title: string;
}

export interface ScheduleOutcome {
  /** Whether it was scheduled with an exact alarm. Web: always `false`. */
  readonly exact: boolean;
  /** Why not, or why inexact — for a diagnostics row or a log line. */
  readonly note?: string;
  readonly scheduled: boolean;
}

function normalise(state: string): PermissionState {
  if (state === "granted" || state === "denied") {
    return state;
  }
  return "prompt";
}

/** Permission to SHOW a notification at all (Android 13+ asks for it). */
export async function notificationPermission(
  request = false
): Promise<PermissionState> {
  try {
    if (!isNative()) {
      if (!("Notification" in window)) {
        return "unsupported";
      }
      const state = request
        ? await Notification.requestPermission()
        : Notification.permission;
      return state === "default" ? "prompt" : state;
    }
    const { LocalNotifications } = await import(
      "@capacitor/local-notifications"
    );
    const status = request
      ? await LocalNotifications.requestPermissions()
      : await LocalNotifications.checkPermissions();
    return normalise(status.display);
  } catch {
    return "unsupported";
  }
}

/** Whether exact alarms are allowed right now. Web: `unsupported`. */
export async function exactAlarmState(): Promise<PermissionState> {
  if (!isNative()) {
    return "unsupported";
  }
  try {
    const { LocalNotifications } = await import(
      "@capacitor/local-notifications"
    );
    const status = await LocalNotifications.checkExactNotificationSetting();
    return normalise(status.exact_alarm);
  } catch {
    // Below Android 12 there is no such setting and every alarm is exact.
    return "granted";
  }
}

/**
 * Sends the user to the system "Alarms & reminders" screen. Resolves with the
 * state after they come back. Web: `unsupported`.
 */
export async function requestExactAlarms(): Promise<PermissionState> {
  if (!isNative()) {
    return "unsupported";
  }
  try {
    const { LocalNotifications } = await import(
      "@capacitor/local-notifications"
    );
    const status = await LocalNotifications.changeExactNotificationSetting();
    return normalise(status.exact_alarm);
  } catch {
    return "unsupported";
  }
}

// Web timers by notification id, so a re-schedule or a cancel reaches them.
const webTimers = new Map<number, ReturnType<typeof setTimeout>>();

/**
 * Schedules one notification. Never throws: a refusal is `scheduled: false`
 * with a note.
 *
 * Web: a `setTimeout` plus the Notification API, so it fires only while the
 * tab is alive. That is the honest limit of a page; the device is where the
 * rest timer is expected to survive a locked screen.
 */
export async function scheduleNotification(
  request: NotificationRequest
): Promise<ScheduleOutcome> {
  const display = await notificationPermission(true);
  if (display !== "granted") {
    return { exact: false, note: `permission ${display}`, scheduled: false };
  }
  try {
    if (!isNative()) {
      clearTimeout(webTimers.get(request.id));
      const delay = Math.max(0, request.at.getTime() - Date.now());
      webTimers.set(
        request.id,
        setTimeout(() => {
          webTimers.delete(request.id);
          const shown = new Notification(request.title, { body: request.body });
          shown.onerror = () =>
            console.warn("[native] web notification failed");
        }, delay)
      );
      return {
        exact: false,
        note: "web: fires only while the tab is open",
        scheduled: true,
      };
    }
    const exact = (await exactAlarmState()) === "granted";
    const { LocalNotifications } = await import(
      "@capacitor/local-notifications"
    );
    // ⚠️ WITHOUT `channelId` THIS LANDS ON CAPACITOR'S `default` CHANNEL AT
    // IMPORTANCE 3, and importance 3 does not peek — so the one notification
    // in this app that is about a number of seconds was the only one that
    // never appeared over the screen. It also showed up in the phone's
    // settings under a channel named "Default", which nobody can recognise or
    // tune. `schedule()` answers `scheduled: true` either way, so no gate saw
    // it; it took a measurement on a real handset (T126, 2026-09-22).
    //
    // The channel is created HERE and not by `ensureChannels` in `push.ts`,
    // which only runs from `enablePush` — the opt-in behind a button. Pointing
    // this at a push channel would work for everyone who enabled push and
    // silently fall back to `default` for everyone who did not.
    await ensureRestChannel(LocalNotifications, request.channelName);
    if (request.actions !== undefined) {
      await ensureRestActions(LocalNotifications, request.actions);
    }
    const result = await LocalNotifications.schedule({
      notifications: [
        {
          ...(request.actions === undefined
            ? {}
            : { actionTypeId: REST_ACTION_TYPE_ID }),
          body: request.body,
          channelId: NOTIFICATION_CHANNEL_REST,
          id: request.id,
          // Checked above: never let the plugin open settings mid-schedule.
          isExactNotification: exact,
          schedule: { allowWhileIdle: true, at: request.at },
          title: request.title,
        },
      ],
    });
    const downgraded = result.warning !== undefined;
    return {
      exact: exact && !downgraded,
      note:
        result.warning?.message ??
        (exact ? undefined : "exact alarms not allowed: scheduled inexact"),
      scheduled: true,
    };
  } catch (error) {
    return {
      exact: false,
      note: error instanceof Error ? error.message : String(error),
      scheduled: false,
    };
  }
}

/**
 * Withdraw a notification on the device: the pending alarm AND a copy the
 * system already posted.
 *
 * ⚠️ `LocalNotifications.cancel` removes PENDING notifications only — its own
 * docs say "Cancel pending notifications" — and a rest that ends with the app
 * open is exactly the case where the OS alarm and the in-page timer fire
 * together. Measured on the container 2026-09-22: the system posted the rest
 * notification at 23:02:30.752, the app's `cancel` arrived 79 ms later, and
 * "Rest's over" sat in the tray from then on while the person was already
 * looking at their next set. Every rest that ended in the foreground left one.
 *
 * Both calls run even if the first refuses: a failed pending-cancel says
 * nothing about whether a posted copy is there to remove.
 */
export async function withdrawOnDevice(
  plugin: Pick<
    LocalNotificationsPlugin,
    "cancel" | "removeDeliveredNotificationsById"
  >,
  id: number
): Promise<void> {
  const [pending, posted] = await Promise.allSettled([
    plugin.cancel({ notifications: [{ id }] }),
    plugin.removeDeliveredNotificationsById({ ids: [id] }),
  ]);
  for (const outcome of [pending, posted]) {
    if (outcome.status === "rejected") {
      console.warn("[native] withdraw notification failed", outcome.reason);
    }
  }
}

export async function cancelNotification(id: number): Promise<void> {
  try {
    if (!isNative()) {
      clearTimeout(webTimers.get(id));
      webTimers.delete(id);
      return;
    }
    const { LocalNotifications } = await import(
      "@capacitor/local-notifications"
    );
    await withdrawOnDevice(LocalNotifications, id);
  } catch (error) {
    console.warn("[native] cancel notification failed", error);
  }
}
